import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faStar,
  faRocket,
  faEye,
  faMagic,
  faHeart,
  faCoins,
  faBrain,
  faArrowRight,
  faUser,
  faUsers,
  faComments
} from '@fortawesome/free-solid-svg-icons';

const HomePage: React.FC = () => {
  const { t } = useTranslation();

  const services = [
    {
      id: 'personal-numerology',
      icon: faUser,
      title: t('personalNumerology'),
      description: t('personalNumerologyDesc'),
      path: '/numerology',
      gradient: 'linear-gradient(135deg, #8b5cf6, #4f46e5)',
      count: 25
    },
    {
      id: 'relationship-numerology',
      icon: faUsers,
      title: t('relationshipNumerology'),
      description: t('relationshipNumerologyDesc'),
      path: '/relationship-numerology',
      gradient: 'linear-gradient(135deg, #ec4899, #dc2626)',
      count: 25
    },
    {
      id: 'astro-numerology',
      icon: faStar,
      title: t('astroNumerology'),
      description: t('astroNumerologyDesc'),
      path: '/astro-numerology',
      gradient: 'linear-gradient(135deg, #3b82f6, #4f46e5)',
      count: 25
    },
    {
      id: 'financial-numerology',
      icon: faCoins,
      title: t('financialNumerology'),
      description: t('financialNumerologyDesc'),
      path: '/financial-numerology',
      gradient: 'linear-gradient(135deg, #10b981, #059669)',
      count: 25
    },
    {
      id: 'healing-numbers',
      icon: faHeart,
      title: t('healingNumbers'),
      description: t('healingNumbersDesc'),
      path: '/healing-numbers',
      gradient: 'linear-gradient(135deg, #06b6d4, #3b82f6)',
      count: 25
    },
    {
      id: 'psychological-numerology',
      icon: faBrain,
      title: t('psychologicalNumerology'),
      description: t('psychologicalNumerologyDesc'),
      path: '/psychological-numerology',
      gradient: 'linear-gradient(135deg, #f97316, #dc2626)',
      count: 25
    }
  ];

  const heroStyle: React.CSSProperties = {
    position: 'relative',
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'hidden'
  };

  const backgroundStyle: React.CSSProperties = {
    position: 'absolute',
    inset: 0,
    background: 'linear-gradient(135deg, #f5f3ff 0%, #e9d5ff 50%, #f5f3ff 100%)'
  };

  const sectionStyle: React.CSSProperties = {
    padding: '80px 20px',
    background: 'rgba(255, 255, 255, 0.5)',
    backdropFilter: 'blur(8px)'
  };

  const containerStyle: React.CSSProperties = {
    maxWidth: '1200px',
    margin: '0 auto',
    padding: '0 20px'
  };

  const gridStyle: React.CSSProperties = {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))',
    gap: '2rem',
    marginTop: '4rem'
  };

  return (
    <div style={{ minHeight: '100vh' }}>
      {/* Hero Section */}
      <section style={heroStyle}>
        <div style={backgroundStyle}></div>

        {/* Floating Elements */}
        <div style={{ position: 'absolute', inset: 0, overflow: 'hidden', pointerEvents: 'none' }}>
          <div 
            className="floating-element" 
            style={{
              position: 'absolute',
              top: '80px',
              left: '40px',
              width: '80px',
              height: '80px',
              background: 'linear-gradient(135deg, rgba(168, 85, 247, 0.2), rgba(147, 51, 234, 0.2))',
              borderRadius: '50%',
              filter: 'blur(40px)'
            }}
          ></div>
        </div>

        <div style={{ position: 'relative', zIndex: 10, ...containerStyle, textAlign: 'center' }}>
          <div style={{ marginBottom: '2rem' }}>
            <div 
              className="floating-element"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '80px',
                height: '80px',
                background: 'linear-gradient(135deg, #8b5cf6, #9333ea)',
                borderRadius: '50%',
                marginBottom: '1.5rem'
              }}
            >
              <FontAwesomeIcon icon={faStar} style={{ color: 'white', fontSize: '2rem' }} />
            </div>
          </div>
          
          <h1 className="gradient-text" style={{ 
            fontSize: 'clamp(2.5rem, 6vw, 4.5rem)', 
            fontWeight: 'bold', 
            marginBottom: '1.5rem',
            whiteSpace: 'pre-line',
            lineHeight: '1.1'
          }}>
            {t('welcome')}
          </h1>
          
          <p style={{ fontSize: 'clamp(1.25rem, 3vw, 1.5rem)', color: '#7c3aed', marginBottom: '1rem', maxWidth: '600px', margin: '0 auto 1rem' }}>
            {t('subtitle')}
          </p>
          
          <p style={{ fontSize: '1.125rem', color: '#6b7280', marginBottom: '2rem', maxWidth: '500px', margin: '0 auto 2rem', lineHeight: 1.6 }}>
            {t('description')}
          </p>
          
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem', justifyContent: 'center', alignItems: 'center' }}>
            <a
              href="#services"
              className="mystical-button"
              style={{ textDecoration: 'none', gap: '0.5rem' }}
            >
              <FontAwesomeIcon icon={faRocket} />
              <span>{t('getStarted')}</span>
            </a>
            
            <Link
              to="/about"
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                color: '#7c3aed',
                fontWeight: '600',
                textDecoration: 'none',
                padding: '12px 32px',
                transition: 'color 0.3s ease'
              }}
            >
              <span>{t('learnMore')}</span>
              <FontAwesomeIcon icon={faArrowRight} />
            </Link>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section style={{ padding: '80px 20px', background: 'linear-gradient(135deg, #f5f3ff, #e9d5ff)' }}>
        <div style={containerStyle}>
          <div style={{ textAlign: 'center', marginBottom: '4rem' }}>
            <h2 className="gradient-text" style={{ fontSize: 'clamp(2rem, 5vw, 2.5rem)', fontWeight: 'bold', marginBottom: '1rem' }}>
              {t('whyChooseUs')}
            </h2>
            <p style={{ fontSize: '1.125rem', color: '#6b7280', maxWidth: '500px', margin: '0 auto' }}>
              Yapay zeka destekli profesyonel numeroloji analizleri ile yaşamınızda fark yaratın
            </p>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '2rem' }}>
            {[
              { 
                icon: faMagic, 
                title: t('professionalAnalysis'), 
                desc: t('professionalAnalysisDesc'), 
                color: 'linear-gradient(135deg, #8b5cf6, #9333ea)' 
              },
              { 
                icon: faHeart, 
                title: t('personalGuidance'), 
                desc: t('personalGuidanceDesc'), 
                color: 'linear-gradient(135deg, #ec4899, #dc2626)' 
              },
              { 
                icon: faEye, 
                title: t('futureInsight'), 
                desc: t('futureInsightDesc'), 
                color: 'linear-gradient(135deg, #3b82f6, #06b6d4)' 
              }
            ].map((feature, index) => (
              <div key={index} style={{ textAlign: 'center', padding: '1.5rem' }}>
                <div 
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: '64px',
                    height: '64px',
                    background: feature.color,
                    borderRadius: '50%',
                    marginBottom: '1rem'
                  }}
                >
                  <FontAwesomeIcon icon={feature.icon} style={{ color: 'white', fontSize: '1.5rem' }} />
                </div>
                <h3 style={{ fontSize: '1.25rem', fontWeight: '600', color: '#374151', marginBottom: '0.75rem' }}>
                  {feature.title}
                </h3>
                <p style={{ color: '#6b7280' }}>
                  {feature.desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" style={sectionStyle}>
        <div style={containerStyle}>
          <div style={{ textAlign: 'center', marginBottom: '4rem' }}>
            <h2 className="gradient-text" style={{ fontSize: 'clamp(2rem, 5vw, 2.5rem)', fontWeight: 'bold', marginBottom: '1rem' }}>
              {t('ourServices')}
            </h2>
            <p style={{ fontSize: '1.125rem', color: '#6b7280', maxWidth: '500px', margin: '0 auto' }}>
              Numeroloji ile kendinizi keşfedin ve yaşamınızın gizemlerini çözün
            </p>
          </div>

          <div style={gridStyle}>
            {services.map((service) => (
              <div
                key={service.id}
                className="mystical-card"
                style={{
                  padding: '1.5rem',
                  transition: 'all 0.3s ease',
                  cursor: 'pointer'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'scale(1.05)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'scale(1)';
                }}
              >
                <div style={{ textAlign: 'center' }}>
                  <div 
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: '64px',
                      height: '64px',
                      background: service.gradient,
                      borderRadius: '50%',
                      marginBottom: '1rem',
                      transition: 'transform 0.3s ease'
                    }}
                  >
                    <FontAwesomeIcon icon={service.icon} style={{ color: 'white', fontSize: '1.5rem' }} />
                  </div>
                  
                  <h3 style={{ fontSize: '1.25rem', fontWeight: '600', color: '#374151', marginBottom: '0.75rem' }}>
                    {service.title}
                  </h3>
                  
                  <p style={{ color: '#6b7280', lineHeight: 1.6, marginBottom: '1.5rem' }}>
                    {service.description}
                  </p>
                  
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <div style={{ display: 'flex', alignItems: 'center', color: '#9ca3af', fontSize: '0.875rem' }}>
                      <FontAwesomeIcon icon={faComments} style={{ marginRight: '0.25rem' }} />
                      <span>{service.count}</span>
                    </div>
                    
                    <Link
                      to={service.path}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        color: '#7c3aed',
                        fontWeight: '500',
                        fontSize: '0.875rem',
                        textDecoration: 'none',
                        transition: 'all 0.3s ease'
                      }}
                    >
                      <span style={{ marginRight: '0.5rem' }}>{t('moreInfo')}</span>
                      <FontAwesomeIcon icon={faArrowRight} />
                    </Link>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomePage; 